/**
 * Form validation utilities for Velox Hint
 * Comprehensive validation for contact form inputs
 */

/**
 * Validate phone number with country code
 * Supports various formats and country codes
 */
export const validatePhoneNumber = (phone, countryCode = "+7") => {
  if (!phone) return { isValid: false, error: "Phone number is required" };

  // Remove spaces, dashes, parentheses
  const cleanPhone = phone.replace(/[\s\-\(\)]/g, '');
  
  // Check if it's just random digits
  if (!/^\+?\d+$/.test(cleanPhone)) {
    return { isValid: false, error: "Invalid phone format" };
  }

  // Country-specific validation
  const validations = {
    "+7": { // Russia/Kazakhstan
      pattern: /^\+?7\d{10}$/,
      minDigits: 11,
      operatorCodes: ['900', '901', '902', '903', '904', '905', '906', '908', '909', 
                      '910', '911', '912', '913', '914', '915', '916', '917', '918', 
                      '919', '920', '921', '922', '923', '924', '925', '926', '927', 
                      '928', '929', '930', '931', '932', '933', '934', '936', '937', 
                      '938', '939', '950', '951', '952', '953', '958', '960', '961', 
                      '962', '963', '964', '965', '966', '967', '968', '969', '977', 
                      '978', '980', '981', '982', '983', '984', '985', '986', '987', 
                      '988', '989', '991', '992', '993', '994', '995', '996', '997', 
                      '999']
    },
    "+1": { // USA/Canada
      pattern: /^\+?1\d{10}$/,
      minDigits: 11
    },
    "+44": { // UK
      pattern: /^\+?44\d{10}$/,
      minDigits: 12
    },
    "+374": { // Armenia
      pattern: /^\+?374\d{8}$/,
      minDigits: 11
    }
  };

  const validation = validations[countryCode];
  if (!validation) {
    if (!countryCode) {
      return { isValid: false, error: "Please select a region" };
    }
    return { isValid: false, error: "Unsupported country code" };
  }

  // Check pattern
  const fullPhone = cleanPhone.startsWith('+') ? cleanPhone : countryCode + cleanPhone;
  if (!validation.pattern.test(fullPhone)) {
    return { isValid: false, error: `Invalid format for ${countryCode}` };
  }

  // Check operator code for Russia
  if (countryCode === "+7" && validation.operatorCodes) {
    const operatorCode = cleanPhone.substring(cleanPhone.length - 10, cleanPhone.length - 7);
    if (!validation.operatorCodes.includes(operatorCode)) {
      return { isValid: false, error: "Invalid operator code" };
    }
  }

  return { isValid: true, formatted: fullPhone };
};

/**
 * Validate name field
 * Checks for proper name format with capital letters
 */
export const validateName = (name) => {
  if (!name || name.trim().length === 0) {
    return { isValid: false, error: "Name is required" };
  }

  const trimmedName = name.trim();

  // Check minimum length
  if (trimmedName.length < 2) {
    return { isValid: false, error: "Name is too short" };
  }

  // Check for random character sequences
  if (isRandomText(trimmedName)) {
    return { isValid: false, error: "Please enter a valid name" };
  }

  // Check for proper name format
  const nameParts = trimmedName.split(/\s+/);
  
  for (const part of nameParts) {
    // Each part should start with capital letter
    if (!/^[A-ZА-ЯЁ]/.test(part)) {
      return { isValid: false, error: "Name must start with a capital letter" };
    }
    
    // Should contain only letters, hyphens, apostrophes
    if (!/^[A-Za-zА-Яа-яЁё\-']+$/.test(part)) {
      return { isValid: false, error: "Name contains invalid characters" };
    }
    
    // Check for repeated characters (more than 2)
    if (/(.)\1{2,}/.test(part)) {
      return { isValid: false, error: "Name contains suspicious patterns" };
    }
  }

  // Check for minimum vowels (real names have vowels)
  const vowelCount = (trimmedName.match(/[aeiouаеёиоуыэюя]/gi) || []).length;
  if (vowelCount === 0) {
    return { isValid: false, error: "Please enter a valid name" };
  }

  return { isValid: true, cleaned: trimmedName };
};

/**
 * Validate message/comment field
 * Checks for meaningful content, not random characters
 */
export const validateMessage = (message, minLength = 10) => {
  if (!message || message.trim().length === 0) {
    return { isValid: false, error: "Message is required" };
  }

  const trimmedMessage = message.trim();

  // Check minimum length
  if (trimmedMessage.length < minLength) {
    return { isValid: false, error: `Message must be at least ${minLength} characters` };
  }

  // Check for random text
  if (isRandomText(trimmedMessage)) {
    return { isValid: false, error: "Please enter a meaningful message" };
  }

  // Check for minimum word count (at least 2 words)
  const words = trimmedMessage.split(/\s+/).filter(w => w.length > 0);
  if (words.length < 2) {
    return { isValid: false, error: "Message is too short" };
  }

  // Check for spaces (real messages have spaces)
  if (trimmedMessage.length > 20 && !trimmedMessage.includes(' ')) {
    return { isValid: false, error: "Please enter a meaningful message" };
  }

  return { isValid: true, cleaned: trimmedMessage };
};

/**
 * Check if text is random/gibberish
 * Uses multiple heuristics to detect random character sequences
 */
const isRandomText = (text) => {
  const cleanText = text.toLowerCase();
  
  // Check 1: Too many consonants in a row (more than 5)
  if (/[bcdfghjklmnpqrstvwxzбвгджзклмнпрстфхцчшщ]{6,}/i.test(cleanText)) {
    return true;
  }

  // Check 2: Mixed case in suspicious way (like vfzrYmAuqMYPqtBb)
  const upperCount = (text.match(/[A-ZА-ЯЁ]/g) || []).length;
  const lowerCount = (text.match(/[a-zа-яё]/g) || []).length;
  if (upperCount > 0 && lowerCount > 0) {
    // Random case mixing pattern
    if (upperCount > 2 && upperCount < text.length * 0.8 && upperCount > text.length * 0.2) {
      return true;
    }
  }

  // Check 3: Very low vowel ratio (real text has ~40% vowels)
  const vowels = (cleanText.match(/[aeiouаеёиоуыэюя]/g) || []).length;
  const consonants = (cleanText.match(/[bcdfghjklmnpqrstvwxzбвгджзклмнпрстфхцчшщ]/g) || []).length;
  const vowelRatio = vowels / (vowels + consonants);
  
  if (cleanText.length > 10 && vowelRatio < 0.15) {
    return true;
  }

  // Check 4: Alternating case pattern
  let caseChanges = 0;
  for (let i = 1; i < text.length; i++) {
    const prevUpper = /[A-ZА-ЯЁ]/.test(text[i-1]);
    const currUpper = /[A-ZА-ЯЁ]/.test(text[i]);
    if (prevUpper !== currUpper) caseChanges++;
  }
  if (caseChanges > text.length * 0.4) {
    return true;
  }

  // Check 5: Entropy check - real text has patterns
  const uniqueChars = new Set(cleanText.split('')).size;
  if (text.length > 15 && uniqueChars / text.length > 0.7) {
    return true; // Too much variety, likely random
  }

  return false;
};

/**
 * Validate email format
 */
export const validateEmail = (email) => {
  if (!email) {
    return { isValid: false, error: "Email is required" };
  }

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailPattern.test(email)) {
    return { isValid: false, error: "Invalid email format" };
  }

  // Check for random text in email
  const localPart = email.split('@')[0];
  if (isRandomText(localPart)) {
    return { isValid: false, error: "Please enter a valid email" };
  }

  return { isValid: true };
};

/**
 * Get list of supported country codes
 */
export const getCountryCodes = () => [
  { code: "+7", country: "RU", label: "Россия" },
  { code: "+7", country: "KZ", label: "Казахстан" },
  { code: "+374", country: "AM", label: "Армения" },
  { code: "+1", country: "US", label: "США" },
  { code: "+44", country: "GB", label: "Великобритания" },
  { code: "+49", country: "DE", label: "Германия" },
  { code: "+33", country: "FR", label: "Франция" },
  { code: "+39", country: "IT", label: "Италия" },
  { code: "+86", country: "CN", label: "Китай" },
];
